import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { isMobileBreakpoint, prefersHeaderDrawer } from '@theme/utilities';

/**
 * @param {HTMLElement} root
 */
export function initSearchDrawerTabs(root) {
  const tabsRoot = root.querySelector('[data-search-drawer-tabs]');
  if (!(tabsRoot instanceof HTMLElement)) return;

  const tabButtons = /** @type {HTMLButtonElement[]} */ (
    Array.from(tabsRoot.querySelectorAll('[role="tab"]'))
  );
  const panels = /** @type {HTMLElement[]} */ (
    Array.from(tabsRoot.querySelectorAll('[role="tabpanel"]'))
  );

  const counts = {
    products: Number(tabsRoot.dataset.countProducts || 0) > 0,
    suggestions: Number(tabsRoot.dataset.countSuggestions || 0) > 0,
    collections: Number(tabsRoot.dataset.countCollections || 0) > 0,
  };

  /** @param {string} tabId */
  const activateTab = (tabId) => {
    tabButtons.forEach((button) => {
      const isActive = button.dataset.tab === tabId;
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.classList.toggle('search-drawer-tabs__tab--active', isActive);
    });

    panels.forEach((panel) => {
      panel.hidden = panel.dataset.panel !== tabId;
    });
  };

  const defaultTab =
    ['products', 'suggestions', 'collections'].find((tab) => counts[/** @type {keyof typeof counts} */ (tab)]) ||
    'products';

  activateTab(defaultTab);

  tabButtons.forEach((button) => {
    button.addEventListener('click', () => {
      if (button.disabled || !button.dataset.tab) return;
      activateTab(button.dataset.tab);
    });
  });
}

/**
 * A custom element that manages the search drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLButtonElement} [closeButton] - The close button.
 *
 * @extends {DialogComponent}
 */
class SearchDrawerComponent extends DialogComponent {
  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleClose);

    if (history.state?.searchDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleClose);
    this.#historyAbortController?.abort();
  }

  #resetTriggerSource() {
    this.classList.remove('search-drawer--nav-compact');
  }

  #setNavigationTrigger() {
    this.classList.add('search-drawer--nav-compact');
  }

  #handleClose = () => {
    this.#resetTriggerSource();
  };

  #handleOpen = () => {
    requestAnimationFrame(() => {
      const input = this.querySelector('.search-input');
      input?.focus();
    });
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.searchDrawerOpen) {
      history.pushState({ searchDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.searchDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  open() {
    this.#resetTriggerSource();
    this.showDialog();
  }

  openFromNavigation() {
    if (prefersHeaderDrawer()) {
      this.#setNavigationTrigger();
    } else {
      this.#resetTriggerSource();
    }

    this.showDialog();
  }

  openFromMenuDrawer() {
    this.#resetTriggerSource();
    this.showDialog();
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('search-drawer-component')) {
  customElements.define('search-drawer-component', SearchDrawerComponent);
}
